import { createContext, useContext } from "react";
import { fetchSortedDatasets } from "../../../redux/Datasets/actions";
import styles from "./styles.module.scss";
import { App, Form, FormInstance } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { getUserInfo } from "../../../redux/User/selectors";
import VLCheckBox from "../../uikit/VLCheckbox";
import { useNavigate } from "react-router-dom";
import {
  useDeleteDatasetApiV1DatasetDatasetIdDelete,
  useHardDeleteDatasetApiV1DatasetDatasetIdHardDelete,
} from "../../../vl-api/vlComponents";

export enum Mode {
  Delete,
  Abort,
}

type IDeleteDatasetContext = {
  deleteDataset: (id: string, mode?: Mode) => void;
};

const DeleteDatasetContext = createContext<IDeleteDatasetContext>({
  deleteDataset: () => null,
});

export const useDeleteDatasetContext = () => useContext(DeleteDatasetContext);

export const DeleteDatasetProvider = ({ children }: { children: any }) => {
  const [form] = Form.useForm<FormValues>();
  const { notification, modal } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSuccess = () => {
    navigate("/datasets");
    dispatch(fetchSortedDatasets(true) as any);
    notification.success({ title: "Dataset deleted successfully" });
    form.resetFields();
  };

  const deleteDatasetMutation = useDeleteDatasetApiV1DatasetDatasetIdDelete({
    onSuccess: handleSuccess,
  });

  const hardDeleteDatasetMutation =
    useHardDeleteDatasetApiV1DatasetDatasetIdHardDelete({
      onSuccess: handleSuccess,
    });

  const handleDelete = (datasetId: string) => {
    const hard = form.getFieldValue("hard");
    if (hard) {
      return hardDeleteDatasetMutation.mutateAsync({
        pathParams: { datasetId },
      });
    } else {
      return deleteDatasetMutation.mutateAsync({ pathParams: { datasetId } });
    }
  };

  const deleteDataset = (datasetId: string, mode: Mode = Mode.Delete) => {
    modal.confirm({
      title: mode === Mode.Delete ? "Delete Dataset" : "Abort Operation",
      width: 500,
      content: (
        <Content
          form={form}
          text={
            mode === Mode.Delete
              ? "delete this dataset"
              : "abort this operation"
          }
        />
      ),
      okText: "Yes",
      onOk: () => handleDelete(datasetId),
      cancelButtonProps: { type: "link" },
      autoFocusButton: null,
    });
  };
  return (
    <DeleteDatasetContext.Provider value={{ deleteDataset }}>
      {children}
    </DeleteDatasetContext.Provider>
  );
};

type FormValues = {
  hard: boolean;
};

const Content = ({
  form,
  text,
}: {
  text: string;
  form: FormInstance<FormValues>;
}) => {
  const user = useSelector(getUserInfo);
  const isInternalUser = user.isInternalUser;
  return (
    <Form form={form} className={styles.deleteDatasetForm}>
      <div tabIndex={0} autoFocus />
      <div>Are you sure you want to {text}?</div>
      <div className={styles.text}>
        Confirming this action will permanently remove the Dataset, images,
        clusters, labels and all their related information.
      </div>
      <Form.Item
        name="hard"
        valuePropName="checked"
        className={styles.checkbox}
        hidden={!isInternalUser}
      >
        <VLCheckBox>Hard delete</VLCheckBox>
      </Form.Item>
    </Form>
  );
};
